import axios from "../axiosConfig";
import {
  getCurrentSessionId,
  getCurrentPSTDateTime,
  ERROR_MESSAGE,
} from "../utils/apiUtils";
import { ChatMessage } from "../types/chat";
import {
  CompareRequestParams,
  ResolveVirtualParams,
  FlagRequestParams,
} from "../types/chatServiceTypes";
import { debuggerService } from "./DebuggerService";

export const makeCompareRequest = async (
  data: CompareRequestParams
): Promise<ChatMessage | { action: string }> => {
  const sessionID = getCurrentSessionId();
  const endpoint = "/api/v1/compare";
  const currentDateTime = getCurrentPSTDateTime();
  const requestBody = {
    query_id: data.queryId || crypto.randomUUID(),
    user_email: data.userEmail,
    session_id: sessionID,
    current_node_id: data.nodeUUID,
    user_message: data.userMessage,
    options: data.options,
    links: data.links,
    actions: data.actions || [],
    user_message_sent_datetime: currentDateTime,
    chatHistory: data.chatHistory,
    idHistory: data.idHistory,
    prefix: "benchmark",
  };
  try {
    const response = await axios.post(endpoint, requestBody);
    const responseData = response.data;
    if (responseData.action) {
      console.log("[RESPONSE UUID] ", responseData.new_reply_node_uuid)
      return { action: responseData.action };
    } else if (responseData.text) {
      return {
        id: crypto.randomUUID(),
        messageId: responseData.query_id,
        text: responseData.text,
        dateTime: new Date(),
        messageType: "bot" as const,
        nodeUuid: responseData.new_reply_node_uuid,
        options: responseData.options || [],
        audio: responseData.audio
          ? Array.isArray(responseData.audio)
            ? responseData.audio
            : [responseData.audio]
          : [],
        media: responseData.media,
        origin: responseData.question,
        endpoint_result: responseData.endpoint_result,
        isFlagged: false,
        comingFrom: responseData.comingFrom || "",
        pruned: responseData.pruned || [],
      };
    } else {
      throw new Error(`Unknown response type: ${JSON.stringify(responseData)}`);
    }
  } catch (error) {
    console.error("Compare request failed:", error);
    return {
      id: crypto.randomUUID(),
      messageId: data.queryId || crypto.randomUUID(),
      text: ERROR_MESSAGE,
      dateTime: new Date(),
      messageType: "bot" as const,
      options: [],
      audio: [],
      media: "",
      endpoint_result: "Error in compare request",
      isFlagged: false,
    };
  }
};

export const makeResolveVirtualRequest = async (
  data: ResolveVirtualParams
): Promise<ChatMessage> => {
  const sessionID = getCurrentSessionId();
  const endpoint = "/api/v1/resolve-virtual";
  const endpointResult =
    data.endpointResult !== undefined && data.endpointResult !== ""
      ? data.endpointResult
      : data.isOptionClick
        ? "user clicked on option"
        : "user clicked on link";
  const currentDateTime = getCurrentPSTDateTime();
  const requestBody = {
    query_id: data.queryId || crypto.randomUUID(),
    user_email: data.userEmail,
    session_id: sessionID,
    current_node_id: data.currentNodeId || data.nodeUUID,
    user_message: data.textClicked,
    requested_node_id: data.nodeUUID,
    endpoint_result: endpointResult,
    user_message_sent_datetime: currentDateTime,
    log_request: data.logRequest || false,
    id: data.id || "",
    prefix: data.prefix || "",
  };
  try {
    if (data.isFirstMessage) {
      try {
        await debuggerService.logSession({
          session_id: sessionID,
          user_email: data.userEmail,
          session_create_time: currentDateTime,
          terms_accepted: true,
          has_a_message: "true",
        });
      } catch (error) {
        console.error("Failed to update session with has_a_message:", error);
      }
    }

    const response = await axios.post(endpoint, requestBody);
    const responseData = response.data;
    console.log("[RESPONSE UUID] ", responseData.new_reply_node_uuid);
    return {
      id: crypto.randomUUID(),
      messageId: responseData.query_id,
      nodeUuid: responseData.new_reply_node_uuid,
      text: responseData.text,
      options: responseData.options,
      audio: responseData.audio
        ? Array.isArray(responseData.audio)
          ? responseData.audio
          : [responseData.audio]
        : [],
      media: responseData.media,
      dateTime: new Date(),
      messageType: "bot" as const,
      origin: data.textClicked,
      endpoint_result: responseData.endpoint_result,
      isFlagged: false,
      comingFrom: responseData.comingFrom || "",
    };
  } catch (error) {
    console.error("Resolve virtual request failed:", error);
    return {
      id: crypto.randomUUID(),
      messageId: data.queryId || crypto.randomUUID(),
      text: ERROR_MESSAGE,
      dateTime: new Date(),
      messageType: "bot" as const,
      options: [],
      origin: "Error in clicking " + data.textClicked,
      endpoint_result: "Error in resolve virtual request",
      isFlagged: false,
    };
  }
};

export const makeFlagMessageRequest = async (
  data: FlagRequestParams
): Promise<{ text: string; query_id: string }> => {
  const endpoint = "/api/v1/flag";
  const requestBody = {
    query_id: data.query_id,
    user_feedback: data.user_feedback,
    session_create_time: data.session_create_time,
  };
  try {
    const response = await axios.post(endpoint, requestBody);
    const responseData = response.data;
    return {
      text: responseData.text,
      query_id: responseData.query_id,
    };
  } catch (error) {
    console.error("Flag message request failed:", error);
    throw error;
  }
};

export const makeLogRequest = async (
  data: Partial<{
    query_id: string;
    session_id: string;
    user_message_sent_datetime: string;
    user_message: string;
    user_email: string;
    user_replying_to_node_uuid: string;
    bot_message_reply_datetime: string;
    bot_message_reply_uuid: string;
    bot_message_reply_text: string;
    bot_message_reply_audio: string | string[];
    bot_message_reply_media: string;
    bot_message_reply_origin: string;
    bot_message_reply_options: Array<[string, string]>;
    bot_message_reply_action: string;
    elapsed_time_to_fetch_audio: number | string;
    elapsed_time_to_fetch_media: number | string;
    is_flagged: string;
    found_correct_node: string;
    user_feedback: string;
    has_been_reviewed: string;
    endpoint: string;
    endpoint_result: string;
    compare_request: string;
    expected_response: string;
    pruned_expected_response: string;
  }> & { query_id: string }
): Promise<void> => {
  const endpoint = "/api/v1/log";
  await axios.post(endpoint, data);
};

export const makeMediaRequest = async (data: {
  query_id: string;
  media_paths: string[];
}): Promise<
  | Blob
  | {
      url: string;
      media_type: string;
      content_type: string;
      elapsed_time: number;
    }
> => {
  const endpoint = "/api/v1/media-object";

  // Convert media_paths array to comma-separated string for query parameters
  const mediaPathsParam = data.media_paths.join(",");
  console.log("[REQUESTED MEDIA] ", mediaPathsParam);

  try {
    const response = await axios.get(endpoint, {
      params: {
        query_id: data.query_id,
        media_paths: mediaPathsParam,
      },
      responseType: "blob",
    });
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/json")) {
      const text = await response.data.text();
      console.log("[MEDIA URL] ", response.data.url)
      return JSON.parse(text);
    } else {
      return response.data;
    }
  } catch (error) {
    console.error("Media request failed:", error);
    throw error;
  }
};

export const getMediaFromChatMessage = async (
  chatMessage: ChatMessage
): Promise<
  | Blob
  | {
      url: string;
      media_type: string;
      content_type: string;
      elapsed_time: number;
    }
  | null
> => {
  if (!chatMessage.media) return null;
  return makeMediaRequest({
    query_id: chatMessage.messageId || chatMessage.id,
    media_paths: [chatMessage.media],
  });
};

export const getAudioFromChatMessage = async (
  chatMessage: ChatMessage
): Promise<Blob | null> => {
  if (!chatMessage.audio || chatMessage.audio.length === 0) return null;
  try {
    const audioResponse = await makeMediaRequest({
      query_id: chatMessage.messageId || chatMessage.id,
      media_paths: chatMessage.audio,
    });
    if (
      audioResponse &&
      typeof audioResponse === "object" &&
      "url" in audioResponse
    ) {
      console.log("[AUDIO FETCHED] ", chatMessage.audio);
      const audioBlob = await fetch(audioResponse.url);
      return await audioBlob.blob();
    } else {
      return audioResponse;
    }
  } catch (error) {
    console.error("Error fetching audio from chat message:", error);
    return null;
  }
};

export const preFetchMediaAndAudio = async (
  chatMessage: ChatMessage
): Promise<ChatMessage> => {
  const result: ChatMessage = { ...chatMessage };
  if (chatMessage.media && chatMessage.messageType === "bot") {
    try {
      const mediaBlob = await getMediaFromChatMessage(chatMessage);
      result.preFetchedMediaBlob = mediaBlob;
      if (mediaBlob instanceof Blob) {
        result.preFetchedMediaUrl = URL.createObjectURL(mediaBlob);
      } else if (
        mediaBlob &&
        typeof mediaBlob === "object" &&
        "url" in mediaBlob
      ) {
        result.preFetchedMediaUrl = mediaBlob.url;
      }
    } catch (error) {
      console.error("Error pre-fetching media:", error);
      result.preFetchedMediaBlob = null;
      result.preFetchedMediaUrl = null;
    }
  }
  if (
    chatMessage.audio &&
    chatMessage.audio.length > 0 &&
    chatMessage.messageType === "bot"
  ) {
    try {
      const audioBlob = await getAudioFromChatMessage(chatMessage);
      result.preFetchedAudioBlob = audioBlob;
    } catch (error) {
      console.error("Error pre-fetching audio:", error);
      result.preFetchedAudioBlob = null;
    }
  }
  return result;
};
